import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Sparkles, Loader2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { HIDE_STICKY_ON } from "@/components/StickyCTA";

const SESSION_KEY = "ma_chatbot_session";

function getSessionId() {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Bonjour ! 🎵 Je suis l'assistant de My Animation. Une question sur un mariage, un anniversaire, le photobooth, nos tarifs ? Je vous réponds en temps réel." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const submit = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/chatbot", { session_id: getSessionId(), message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply || "..." }]);
    } catch (e2) {
      setMessages((m) => [...m, { role: "assistant", content: "Désolé, je rencontre un souci technique. Vous pouvez nous appeler au 07 69 22 49 56." }]);
    } finally {
      setSending(false);
    }
  };

  if (HIDE_STICKY_ON.some((r) => r.test(pathname))) return null;
  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="chatbot-open"
          style={{ bottom: "max(1rem, env(safe-area-inset-bottom))" }}
          className="fixed right-4 lg:!bottom-6 lg:right-6 z-40 w-12 h-12 lg:w-14 lg:h-14 rounded-full bg-[#DCB457] text-black shadow-[0_8px_30px_rgba(220,180,87,0.5)] hover:bg-[#e6c454] transition-all duration-300 flex items-center justify-center group"
          aria-label="Ouvrir le chat"
        >
          <MessageCircle size={22} strokeWidth={1.5} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-black animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-3 left-3 right-3 sm:left-auto sm:bottom-6 sm:right-6 z-50 sm:w-[400px] h-[560px] max-h-[85vh] bg-[#0a0a0a] border border-[#DCB457]/35 shadow-[0_20px_60px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] border-b border-[#DCB457]/25 px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full border border-[#DCB457]/50 flex items-center justify-center text-[#DCB457]">
                <Sparkles size={16} strokeWidth={1.3} />
              </div>
              <div>
                <p className="font-serif-luxe text-base text-white leading-none">Assistant My Animation</p>
                <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80 mt-1 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> En ligne 24/7
                </p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chatbot-close" className="text-white/55 hover:text-white transition">
              <X size={18} strokeWidth={1.5} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#DCB457] text-black"
                      : "bg-black/40 border border-[#DCB457]/15 text-white/85"
                  }`}
                  style={{ borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px" }}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-black/40 border border-[#DCB457]/15 px-4 py-2.5 inline-flex items-center gap-2 text-white/70 text-sm">
                  <Loader2 size={14} className="animate-spin text-[#DCB457]" />
                  L'assistant écrit…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={submit} className="border-t border-[#DCB457]/15 p-3 flex items-end gap-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
              }}
              placeholder="Votre question…"
              rows={1}
              data-testid="chatbot-input"
              className="flex-1 bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white focus:border-[#DCB457] focus:outline-none resize-none max-h-24"
            />
            <button
              type="submit"
              disabled={!input.trim() || sending}
              data-testid="chatbot-send"
              className="shrink-0 w-10 h-10 bg-[#DCB457] text-black hover:bg-[#e6c454] transition disabled:opacity-40 flex items-center justify-center"
              aria-label="Envoyer"
            >
              <Send size={15} strokeWidth={1.5} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
